import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import {
  config,
} from "../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| PLATFORM API
|--------------------------------------------------------------------------
*/

const platformApi =
  new API({
    tokenKey:
      "platformAccessToken",
  });


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorPayload = (
  error,
  fallbackMessage
) => {

  return {
    message:
      error?.message ||
      fallbackMessage,

    errorCode:
      error?.errorCode ||
      error?.code ||
      null,

    statusCode:
      error?.statusCode ||
      error?.status ||
      error?.code ||
      null,

    details:
      error?.details ||
      null,
  };

};


/*
|--------------------------------------------------------------------------
| RESPONSE VALIDATION
|--------------------------------------------------------------------------
*/

const validateResponse = (
  response,
  fallbackMessage
) => {

  if (
    response?.success === false ||
    response?.status === "failed" ||
    response?.status === "error"
  ) {

    const error =
      new Error(
        response?.message ||
        fallbackMessage
      );

    error.code =
      response?.code;

    error.errorCode =
      response?.errorCode;

    error.details =
      response?.details;

    throw error;

  }

  return response;

};


/*
|--------------------------------------------------------------------------
| QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
  params = {}
) => {

  const query =
    new URLSearchParams();


  Object.entries(
    params || {}
  ).forEach(
    ([
      key,
      value,
    ]) => {

      if (
        value === undefined ||
        value === null ||
        value === ""
      ) {
        return;
      }


      query.append(
        key,
        String(
          value
        )
      );

    }
  );


  const queryString =
    query.toString();


  return queryString
    ? `?${queryString}`
    : "";

};


/*
|--------------------------------------------------------------------------
| FETCH BUSINESSES
|--------------------------------------------------------------------------
|
| GET /v1/platform/business
|--------------------------------------------------------------------------
*/

export const fetchPlatformBusinesses =
  createAsyncThunk(
    "platformBusiness/fetchBusinesses",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            `${
              config.getPlatformBusinesses
            }${
              buildQueryString(
                params
              )
            }`
          );


        validateResponse(
          response,
          "Unable to fetch businesses."
        );


        return {

          businesses:
            Array.isArray(
              response?.data
            )
              ? response.data
              : [],

          pagination:
            response?.pagination ||
            {
              page:
                Number(
                  params?.page
                ) ||
                1,

              limit:
                Number(
                  params?.limit
                ) ||
                20,

              total:
                0,

              totalPages:
                1,
            },

        };

      } catch (
        error
      ) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch businesses."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| BUSINESS DETAILS
|--------------------------------------------------------------------------
*/

export const fetchPlatformBusinessById =
  createAsyncThunk(
    "platformBusiness/fetchBusinessById",

    async (
      id,
      {
        rejectWithValue,
      }
    ) => {

      try {

        if (!id) {

          return rejectWithValue({
            message:
              "Business ID is required.",
          });

        }


        const response =
          await platformApi.get(
            config.getPlatformBusinessById(
              id
            )
          );


        validateResponse(
          response,
          "Unable to fetch business."
        );


        return (
          response?.data ||
          null
        );

      } catch (
        error
      ) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch business."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| CREATE BUSINESS
|--------------------------------------------------------------------------
|
| Body:
|
| {
|   business: {...},
|   owner: {...}
| }
|--------------------------------------------------------------------------
*/

export const createPlatformBusiness =
  createAsyncThunk(
    "platformBusiness/createBusiness",

    async (
      payload,
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.post(
            config.createPlatformBusiness,
            payload
          );


        validateResponse(
          response,
          "Unable to create business."
        );


        return (
          response?.data ||
          null
        );

      } catch (
        error
      ) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to create business."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| UPDATE BUSINESS
|--------------------------------------------------------------------------
*/

export const updatePlatformBusiness =
  createAsyncThunk(
    "platformBusiness/updateBusiness",

    async (
      {
        id,
        data,
      },
      {
        rejectWithValue,
      }
    ) => {

      try {

        if (!id) {

          return rejectWithValue({
            message:
              "Business ID is required.",
          });

        }


        const response =
          await platformApi.put(
            config.updatePlatformBusiness(
              id
            ),
            data
          );


        validateResponse(
          response,
          "Unable to update business."
        );


        return (
          response?.data ||
          null
        );

      } catch (
        error
      ) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to update business."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| ACTIVATE BUSINESS
|--------------------------------------------------------------------------
*/

export const activatePlatformBusiness =
  createAsyncThunk(
    "platformBusiness/activateBusiness",

    async (
      id,
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.patch(
            config.activatePlatformBusiness(
              id
            ),
            {}
          );


        validateResponse(
          response,
          "Unable to activate business."
        );


        return (
          response?.data ||
          null
        );

      } catch (
        error
      ) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to activate business."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| SUSPEND BUSINESS
|--------------------------------------------------------------------------
*/

export const suspendPlatformBusiness =
  createAsyncThunk(
    "platformBusiness/suspendBusiness",

    async (
      id,
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.patch(
            config.suspendPlatformBusiness(
              id
            ),
            {}
          );


        validateResponse(
          response,
          "Unable to suspend business."
        );


        return (
          response?.data ||
          null
        );

      } catch (
        error
      ) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to suspend business."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

  businesses:
    [],

  pagination: {
    page:
      1,

    limit:
      20,

    total:
      0,

    totalPages:
      1,
  },


  /*
  |--------------------------------------------------------------------------
  | FILTERS
  |--------------------------------------------------------------------------
  |
  | Backend currently supports:
  |
  | page
  | limit
  | search
  | status
  |--------------------------------------------------------------------------
  */

  filters: {
    search:
      "",

    status:
      "",

    page:
      1,

    limit:
      20,
  },


  selectedBusiness:
    null,

  createdBusiness:
    null,


  listLoading:
    false,

  detailsLoading:
    false,

  createLoading:
    false,

  updateLoading:
    false,

  statusLoading:
    false,

  statusBusinessId:
    null,


  listError:
    null,

  detailsError:
    null,

  createError:
    null,

  updateError:
    null,

  statusError:
    null,

};


/*
|--------------------------------------------------------------------------
| GET BUSINESS ID
|--------------------------------------------------------------------------
*/

const getBusinessId = (
  business
) => {

  return (
    business?._id ||
    business?.id ||
    null
  );

};


/*
|--------------------------------------------------------------------------
| REPLACE LIST ITEM
|--------------------------------------------------------------------------
*/

const replaceBusinessInList = (
  state,
  business
) => {

  const businessId =
    getBusinessId(
      business
    );


  if (
    !businessId
  ) {
    return;
  }


  const index =
    state.businesses.findIndex(
      (
        item
      ) =>
        String(
          getBusinessId(
            item
          )
        ) ===
        String(
          businessId
        )
    );


  if (
    index >=
    0
  ) {

    state.businesses[
      index
    ] = {
      ...state.businesses[
        index
      ],

      ...business,
    };

  }

};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformBusinessSlice =
  createSlice({

    name:
      "platformBusiness",

    initialState,

    reducers: {

      /*
      |--------------------------------------------------------------------------
      | FILTER
      |--------------------------------------------------------------------------
      */

      setPlatformBusinessFilter: (
        state,
        action
      ) => {

        const {
          key,
          value,
        } =
          action.payload ||
          {};


        if (!key) {
          return;
        }


        state.filters[
          key
        ] =
          value;


        if (
          key !==
          "page"
        ) {

          state.filters.page =
            1;

        }

      },


      /*
      |--------------------------------------------------------------------------
      | SET FILTERS
      |--------------------------------------------------------------------------
      */

      setPlatformBusinessFilters: (
        state,
        action
      ) => {

        state.filters = {
          ...state.filters,
          ...(action.payload ||
            {}),
        };

      },


      /*
      |--------------------------------------------------------------------------
      | RESET FILTERS
      |--------------------------------------------------------------------------
      */

      resetPlatformBusinessFilters: (
        state
      ) => {

        state.filters = {
          search:
            "",

          status:
            "",

          page:
            1,

          limit:
            20,
        };

      },


      /*
      |--------------------------------------------------------------------------
      | CLEAR SELECTED
      |--------------------------------------------------------------------------
      */

      clearSelectedPlatformBusiness: (
        state
      ) => {

        state.selectedBusiness =
          null;

        state.detailsError =
          null;

      },


      /*
      |--------------------------------------------------------------------------
      | CLEAR CREATED
      |--------------------------------------------------------------------------
      */

      clearCreatedPlatformBusiness: (
        state
      ) => {

        state.createdBusiness =
          null;

        state.createError =
          null;

      },


      /*
      |--------------------------------------------------------------------------
      | CLEAR ERRORS
      |--------------------------------------------------------------------------
      */

      clearPlatformBusinessErrors: (
        state
      ) => {

        state.listError =
          null;

        state.detailsError =
          null;

        state.createError =
          null;

        state.updateError =
          null;

        state.statusError =
          null;

      },


      /*
      |--------------------------------------------------------------------------
      | RESET
      |--------------------------------------------------------------------------
      */

      resetPlatformBusiness:
        () =>
          initialState,

    },


    extraReducers: (
      builder
    ) => {

      /*
      |--------------------------------------------------------------------------
      | LIST
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformBusinesses.pending,
          (
            state
          ) => {

            state.listLoading =
              true;

            state.listError =
              null;

          }
        )

        .addCase(
          fetchPlatformBusinesses.fulfilled,
          (
            state,
            action
          ) => {

            state.listLoading =
              false;

            state.listError =
              null;

            state.businesses =
              action.payload
                ?.businesses ||
              [];

            state.pagination = {
              ...state.pagination,
              ...(action.payload
                ?.pagination ||
                {}),
            };

          }
        )

        .addCase(
          fetchPlatformBusinesses.rejected,
          (
            state,
            action
          ) => {

            state.listLoading =
              false;

            state.listError =
              action.payload ||
              {
                message:
                  "Unable to fetch businesses.",
              };

          }
        );


      /*
      |--------------------------------------------------------------------------
      | DETAILS
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformBusinessById.pending,
          (
            state
          ) => {

            state.detailsLoading =
              true;

            state.detailsError =
              null;

          }
        )

        .addCase(
          fetchPlatformBusinessById.fulfilled,
          (
            state,
            action
          ) => {

            state.detailsLoading =
              false;

            state.detailsError =
              null;

            state.selectedBusiness =
              action.payload ||
              null;

          }
        )

        .addCase(
          fetchPlatformBusinessById.rejected,
          (
            state,
            action
          ) => {

            state.detailsLoading =
              false;

            state.detailsError =
              action.payload ||
              null;

          }
        );


      /*
      |--------------------------------------------------------------------------
      | CREATE
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          createPlatformBusiness.pending,
          (
            state
          ) => {

            state.createLoading =
              true;

            state.createError =
              null;

            state.createdBusiness =
              null;

          }
        )

        .addCase(
          createPlatformBusiness.fulfilled,
          (
            state,
            action
          ) => {

            state.createLoading =
              false;

            state.createError =
              null;

            state.createdBusiness =
              action.payload ||
              null;

          }
        )

        .addCase(
          createPlatformBusiness.rejected,
          (
            state,
            action
          ) => {

            state.createLoading =
              false;

            state.createError =
              action.payload ||
              null;

          }
        );


      /*
      |--------------------------------------------------------------------------
      | UPDATE
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          updatePlatformBusiness.pending,
          (
            state
          ) => {

            state.updateLoading =
              true;

            state.updateError =
              null;

          }
        )

        .addCase(
          updatePlatformBusiness.fulfilled,
          (
            state,
            action
          ) => {

            state.updateLoading =
              false;

            state.updateError =
              null;


            const business =
              action.payload;


            state.selectedBusiness =
              business;


            replaceBusinessInList(
              state,
              business
            );

          }
        )

        .addCase(
          updatePlatformBusiness.rejected,
          (
            state,
            action
          ) => {

            state.updateLoading =
              false;

            state.updateError =
              action.payload ||
              null;

          }
        );


      /*
      |--------------------------------------------------------------------------
      | ACTIVATE
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          activatePlatformBusiness.pending,
          (
            state,
            action
          ) => {

            state.statusLoading =
              true;

            state.statusError =
              null;

            state.statusBusinessId =
              action.meta.arg;

          }
        )

        .addCase(
          activatePlatformBusiness.fulfilled,
          (
            state,
            action
          ) => {

            state.statusLoading =
              false;

            state.statusBusinessId =
              null;


            const business =
              action.payload;


            replaceBusinessInList(
              state,
              business
            );


            if (
              String(
                getBusinessId(
                  state.selectedBusiness
                )
              ) ===
              String(
                getBusinessId(
                  business
                )
              )
            ) {

              state.selectedBusiness = {
                ...state.selectedBusiness,
                ...business,
              };

            }

          }
        )

        .addCase(
          activatePlatformBusiness.rejected,
          (
            state,
            action
          ) => {

            state.statusLoading =
              false;

            state.statusBusinessId =
              null;

            state.statusError =
              action.payload ||
              null;

          }
        );


      /*
      |--------------------------------------------------------------------------
      | SUSPEND
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          suspendPlatformBusiness.pending,
          (
            state,
            action
          ) => {

            state.statusLoading =
              true;

            state.statusError =
              null;

            state.statusBusinessId =
              action.meta.arg;

          }
        )

        .addCase(
          suspendPlatformBusiness.fulfilled,
          (
            state,
            action
          ) => {

            state.statusLoading =
              false;

            state.statusBusinessId =
              null;


            const business =
              action.payload;


            replaceBusinessInList(
              state,
              business
            );


            if (
              String(
                getBusinessId(
                  state.selectedBusiness
                )
              ) ===
              String(
                getBusinessId(
                  business
                )
              )
            ) {

              state.selectedBusiness = {
                ...state.selectedBusiness,
                ...business,
              };

            }

          }
        )

        .addCase(
          suspendPlatformBusiness.rejected,
          (
            state,
            action
          ) => {

            state.statusLoading =
              false;

            state.statusBusinessId =
              null;

            state.statusError =
              action.payload ||
              null;

          }
        );

    },

  });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  setPlatformBusinessFilter,
  setPlatformBusinessFilters,
  resetPlatformBusinessFilters,
  clearSelectedPlatformBusiness,
  clearCreatedPlatformBusiness,
  clearPlatformBusinessErrors,
  resetPlatformBusiness,
} =
  platformBusinessSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectPlatformBusinesses = (
  state
) =>
  state.platformBusiness
    ?.businesses ||
  [];


export const selectPlatformBusinessPagination = (
  state
) =>
  state.platformBusiness
    ?.pagination ||
  {};


export const selectPlatformBusinessFilters = (
  state
) =>
  state.platformBusiness
    ?.filters ||
  {};


export const selectSelectedPlatformBusiness = (
  state
) =>
  state.platformBusiness
    ?.selectedBusiness ||
  null;


export const selectCreatedPlatformBusiness = (
  state
) =>
  state.platformBusiness
    ?.createdBusiness ||
  null;


export const selectPlatformBusinessListLoading = (
  state
) =>
  Boolean(
    state.platformBusiness
      ?.listLoading
  );


export const selectPlatformBusinessDetailsLoading = (
  state
) =>
  Boolean(
    state.platformBusiness
      ?.detailsLoading
  );


export const selectPlatformBusinessCreateLoading = (
  state
) =>
  Boolean(
    state.platformBusiness
      ?.createLoading
  );


export const selectPlatformBusinessUpdateLoading = (
  state
) =>
  Boolean(
    state.platformBusiness
      ?.updateLoading
  );


export const selectPlatformBusinessStatusLoading = (
  state
) =>
  Boolean(
    state.platformBusiness
      ?.statusLoading
  );


export const selectPlatformBusinessStatusBusinessId = (
  state
) =>
  state.platformBusiness
    ?.statusBusinessId ||
  null;


export default platformBusinessSlice.reducer;